/**
 * VoiceEntry — Tela de entrada rápida por voz
 * Botão grande de microfone com animação de gravação.
 * Ao parar, envia o áudio para ai.quickVoiceEntry e redireciona para VoiceConfirm.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, MicOff, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { useI18n } from "@/contexts/I18nContext";

type Stage = "idle" | "recording" | "processing" | "error";

export default function VoiceEntry() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [stage, setStage] = useState<Stage>("idle");
  const [recordingTime, setRecordingTime] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const quickVoiceMutation = trpc.ai.quickVoiceEntry.useMutation({
    onSuccess: (data) => {
      // Passa os dados extraídos via state para VoiceConfirm
      navigate("/voice/confirm", { state: data });
    },
    onError: () => {
      setStage("error");
      setErrorMsg(t.voice.error);
    },
  });

  // Limpa timer ao desmontar
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  async function startRecording() {
    setErrorMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "";

      if (!mimeType) {
        setStage("error");
        setErrorMsg(t.voice.unsupportedBrowser);
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (timerRef.current) clearInterval(timerRef.current);

        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size > 16 * 1024 * 1024) {
          setStage("error");
          setErrorMsg(t.voice.error);
          return;
        }

        setStage("processing");
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(",")[1];
          quickVoiceMutation.mutate({ base64, mimeType });
        };
        reader.readAsDataURL(blob);
      };

      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setStage("recording");
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((t) => {
          if (t >= 120) {
            stopRecording();
            return t;
          }
          return t + 1;
        });
      }, 1000);
    } catch (err: any) {
      if (err?.name === "NotAllowedError" || err?.name === "PermissionDeniedError") {
        setStage("error");
        setErrorMsg(t.voice.microphoneDenied);
      } else {
        setStage("error");
        setErrorMsg(t.voice.noMic);
      }
    }
  }

  function stopRecording() {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }

  function handleMicClick() {
    if (stage === "idle" || stage === "error") {
      startRecording();
    } else if (stage === "recording") {
      stopRecording();
    }
  }

  const formatTime = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center relative px-6">
      {/* Botão fechar */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-6 left-6 w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
        aria-label={t.common.close}
      >
        <X className="w-5 h-5 text-muted-foreground" />
      </button>

      {/* Título */}
      <div className="text-center mb-12">
        <h1 className="font-serif text-3xl font-semibold text-foreground mb-2">
          {stage === "processing" ? t.voice.processing : t.voice.speakTransaction}
        </h1>
        <p className="text-muted-foreground text-sm max-w-xs" role="status" aria-live="polite">
          {stage === "idle" && t.voice.idleHint}
          {stage === "recording" && t.voice.recordingHint}
          {stage === "processing" && t.voice.processing}
          {stage === "error" && errorMsg}
        </p>
      </div>

      {/* Botão de microfone */}
      <div className="relative flex items-center justify-center mb-10">
        {/* Ondas de animação durante gravação */}
        {stage === "recording" && (
          <>
            <span className="absolute w-40 h-40 rounded-full bg-primary/10 animate-ping" style={{ animationDuration: "1.2s" }} />
            <span className="absolute w-52 h-52 rounded-full bg-primary/5 animate-ping" style={{ animationDuration: "1.8s" }} />
          </>
        )}

        <button
          onClick={handleMicClick}
          disabled={stage === "processing"}
          className={cn(
            "relative w-32 h-32 rounded-full flex items-center justify-center shadow-2xl transition-all duration-200 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/50",
            stage === "recording"
              ? "bg-red-500 hover:bg-red-600 scale-110"
              : stage === "processing"
              ? "bg-muted cursor-not-allowed"
              : stage === "error"
              ? "bg-destructive hover:bg-destructive/90"
              : "bg-primary hover:bg-primary/90"
          )}
          aria-label={stage === "recording" ? t.voice.stopRecording : t.voice.startRecording}
        >
          {stage === "processing" ? (
            <Loader2 className="w-14 h-14 text-white animate-spin" />
          ) : stage === "recording" ? (
            <MicOff className="w-14 h-14 text-white" />
          ) : (
            <Mic className="w-14 h-14 text-white" />
          )}
        </button>
      </div>

      {/* Timer de gravação */}
      {stage === "recording" && (
        <div className="flex items-center gap-2 text-red-500 font-mono text-xl font-semibold">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
          {formatTime(recordingTime)}
        </div>
      )}

      {/* Dicas */}
      {stage === "idle" && (
        <div className="mt-10 grid grid-cols-1 gap-3 w-full max-w-sm">
          {[
            { emoji: "🛒", text: t.voice.exampleExpense },
            { emoji: "💰", text: t.voice.exampleIncome },
            { emoji: "🚗", text: t.voice.exampleFuel },
          ].map(({ emoji, text }) => (
            <div key={text} className="flex items-center gap-3 bg-muted/50 rounded-xl px-4 py-3 text-sm text-muted-foreground">
              <span className="text-xl">{emoji}</span>
              <span>{text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Botão tentar novamente */}
      {stage === "error" && (
        <div className="mt-6 flex flex-col gap-3 w-full max-w-sm">
          <Button
            variant="outline"
            onClick={() => { setStage("idle"); setErrorMsg(""); }}
          >
            {t.voice.retry}
          </Button>
          <Button variant="secondary" onClick={() => navigate("/transactions")}>
            {t.voice.manualEntry}
          </Button>
        </div>
      )}
    </div>
  );
}
